import { useId, useState, type FormEvent } from 'react';
import type { ProviderId } from '@trivio/contracts';
import { Button } from '@tervior-tech/tervior-shared-ui-react';
import { Card } from './ui/Card';
import { Label } from './ui/Label';
import { Input } from './ui/Input';
import { FieldError } from './ui/FieldError';
import { Badge } from './ui/Badge';
import { ApiProblemError, putProviderCredentials, type ProviderBalance, type ProviderConnection } from '../lib/api-client';
import { providerConnectionStatusPt } from '../utils/enums-ptbr';
import { formatCentsToBRL } from '../utils/money';

export interface ProviderCardConfig {
  id: ProviderId;
  name: string;
  homepage: string;
  comingSoon?: boolean;
  /**
   * Shown under the field label to disambiguate which value the provider's
   * OWN dashboard calls this by, when it differs from our generic "Chave de
   * API" phrasing — Frenet's painel shows three distinct fields (Chave,
   * Senha, Token) and only o Token is what `frenet.adapter.ts#
   * validateCredentials` actually sends as the `token` header (found via a
   * real onboarding attempt, 2026-08-26 — a lojista pasted "Chave" first,
   * which Frenet's real API rejected with a 422). Omit for a provider whose
   * own dashboard doesn't have this ambiguity.
   */
  credentialHint?: string;
}

/**
 * The full provider catalog — same three entries `ProvidersStep.tsx`
 * (onboarding) always rendered, now shared with `ProvidersPage.tsx`
 * (TTP-244) so both screens list providers identically instead of keeping
 * two copies in sync by hand. Official homepages only (no guessed signup
 * deep-paths).
 */
export const PROVIDER_CATALOG: ProviderCardConfig[] = [
  {
    id: 'frenet',
    name: 'Frenet',
    homepage: 'https://www.frenet.com.br/',
    credentialHint: 'Use o campo "Token" do painel da Frenet — não a Chave nem a Senha.',
  },
  { id: 'superfrete', name: 'SuperFrete', homepage: 'https://superfrete.com/' },
  { id: 'melhor_envio', name: 'Melhor Envio', homepage: 'https://melhorenvio.com.br/', comingSoon: true },
];

interface ProviderConnectCardProps {
  config: ProviderCardConfig;
  /** The Trivio API key authorizing the `PUT .../credentials` call — the onboarding `trv_live_` key in `ProvidersStep.tsx`, the dashboard session key (TTP-244) in `ProvidersPage.tsx`. Never the `trv_test_` key — see providers.service.ts#putCredentials's `isSandbox` branch. */
  apiKey: string;
  connection: ProviderConnection | null;
  onConnected: (connection: ProviderConnection) => void;
  /**
   * Only passed by `ProvidersPage.tsx` (TTP-244's post-onboarding
   * `/providers`) — onboarding's `ProvidersStep.tsx` never offers a
   * "desconectar" action, so its behavior is unchanged by this prop being
   * absent.
   */
  onDisconnect?: () => void;
  disconnecting?: boolean;
  /** Shows `connected_at`/`last_validated_at` — only meaningful (and only requested) in the dashboard's `/providers`, not during onboarding. */
  showConnectionDetails?: boolean;
  /**
   * Saldo/limite de etiquetas (TTP-267) — only fetched/passed by
   * `ProvidersPage.tsx` (`GET /v1/providers/{provider}/balance`), and only
   * for a `status: 'active'` connection; onboarding's `ProvidersStep.tsx`
   * never passes this. `undefined` (not fetched/not applicable) renders
   * nothing; a fetched `ProviderBalance` with a `null` field renders "não
   * disponível" for that field — NEVER `0` (CLAUDE.md: não forçar zero que
   * pareceria "sem saldo").
   */
  balance?: ProviderBalance;
  /** `true` while `GET .../balance` is in flight for this specific provider — shows "Carregando…" instead of stale/absent values. */
  balanceLoading?: boolean;
}

const NOT_AVAILABLE_LABEL = 'não disponível';

function formatBalanceCents(cents: number | null): string {
  return cents === null ? NOT_AVAILABLE_LABEL : formatCentsToBRL(cents);
}

function formatLabelLimit(limit: number | null): string {
  return limit === null ? NOT_AVAILABLE_LABEL : limit.toLocaleString('pt-BR');
}

/**
 * Provider connect/reconnect card — extracted from `ProvidersStep.tsx`
 * (TTP-243 onboarding, Tela 3) so it can be reused, unmodified in behavior,
 * by TTP-244's `/providers` (see that issue's "Reaproveite o formulário de
 * conectar provedor do onboarding"). Same `PUT`-with-`200`/`422`-feedback
 * logic in both places — never duplicated.
 */
export function ProviderConnectCard({
  config,
  apiKey,
  connection,
  onConnected,
  onDisconnect,
  disconnecting = false,
  showConnectionDetails = false,
  balance,
  balanceLoading = false,
}: ProviderConnectCardProps) {
  const inputId = useId();
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const isActive = connection?.status === 'active';
  const isInvalid = connection?.status === 'invalid_credentials';

  async function handleConnect(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!apiKeyInput.trim()) {
      setError('Cole a chave de API do provedor.');
      return;
    }
    setError(undefined);
    setValidating(true);
    try {
      const result = await putProviderCredentials(apiKey, config.id, apiKeyInput.trim());
      onConnected(result);
    } catch (err) {
      if (err instanceof ApiProblemError && err.status === 422) {
        setError('Chave inválida — verifique no painel do provedor.');
      } else {
        setError('Não foi possível validar a credencial agora. Tente novamente.');
      }
    } finally {
      // Never keep the pasted credential around after the call resolves,
      // success or failure (CLAUDE.md / issue UI rule).
      setApiKeyInput('');
      setValidating(false);
    }
  }

  const facts: Array<{ k: string; v: string }> = [];
  if (showConnectionDetails && connection) {
    facts.push(
      { k: 'Status', v: providerConnectionStatusPt(connection.status) },
      { k: 'Conectado em', v: new Date(connection.connected_at).toLocaleString('pt-BR') },
      { k: 'Última validação', v: new Date(connection.last_validated_at).toLocaleString('pt-BR') },
    );
  }

  return (
    <Card className="flex flex-col">
      <div className="flex flex-col gap-3 border-b border-border p-5">
        <div className="flex items-start justify-between gap-3">
          <span className="text-lg font-medium text-foreground">{config.name}</span>
          {config.comingSoon ? (
            <Badge variant="neutral">Em breve</Badge>
          ) : isActive ? (
            <Badge variant="success">Conectado</Badge>
          ) : isInvalid ? (
            <Badge variant="error">Credencial inválida</Badge>
          ) : showConnectionDetails ? (
            // Onboarding's Tela 3 never shows this badge for the
            // not-yet-attempted state (unchanged behavior there) — only the
            // dashboard's /providers, which always lists every provider
            // including ones never connected, benefits from an explicit
            // "Desconectado" instead of no badge at all.
            <Badge variant="neutral">Desconectado</Badge>
          ) : null}
        </div>
        <a
          href={config.homepage}
          target="_blank"
          rel="noreferrer noopener"
          className="self-start text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          Criar conta
        </a>
      </div>

      <div className="flex flex-1 flex-col gap-4 p-5">
        {isInvalid && showConnectionDetails ? (
          <p className="text-sm font-medium text-accent">
            Credencial inválida no provedor — reconecte sua conta para continuar cotando por aqui.
          </p>
        ) : null}

        {facts.length > 0 ? (
          <dl className="flex flex-col gap-2">
            {facts.map((fact) => (
              <div key={fact.k} className="flex items-baseline justify-between gap-3 text-sm">
                <dt className="text-muted-foreground">{fact.k}</dt>
                <dd className="font-mono text-xs text-foreground">{fact.v}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        {isActive && (balance !== undefined || balanceLoading) ? (
          <div className="grid grid-cols-2 gap-4 border border-border bg-muted/20 p-4">
            <div className="flex flex-col gap-1">
              <span className="font-mono text-xs font-medium uppercase tracking-widest text-muted-foreground">Saldo</span>
              <span className="text-2xl font-medium text-foreground">
                {balanceLoading ? 'Carregando…' : formatBalanceCents(balance?.balance_cents ?? null)}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-mono text-xs font-medium uppercase tracking-widest text-muted-foreground">Limite de etiquetas</span>
              <span className="text-2xl font-medium text-foreground">
                {balanceLoading ? 'Carregando…' : formatLabelLimit(balance?.label_limit ?? null)}
              </span>
            </div>
          </div>
        ) : null}

        <div className="mt-auto flex flex-col gap-3">
          {config.comingSoon ? (
            <p className="text-sm text-muted-foreground">Integração ainda não disponível.</p>
          ) : isActive ? (
            <>
              <p className="text-sm text-muted-foreground">
                Conectado — a credencial fica guardada com segurança e nunca é exibida novamente aqui.
              </p>
              {onDisconnect ? (
                <Button type="button" variant="outline" onClick={onDisconnect} disabled={disconnecting} className="self-start">
                  {disconnecting ? 'Desconectando…' : 'Desconectar'}
                </Button>
              ) : null}
            </>
          ) : (
            <form className="flex flex-col gap-2" onSubmit={handleConnect} noValidate>
              <Label htmlFor={inputId}>Chave de API da {config.name}</Label>
              {config.credentialHint ? <p className="text-xs text-muted-foreground">{config.credentialHint}</p> : null}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                <Input
                  id={inputId}
                  type="password"
                  autoComplete="off"
                  value={apiKeyInput}
                  invalid={Boolean(error)}
                  aria-describedby={error ? `${inputId}-error` : undefined}
                  onChange={(event) => setApiKeyInput(event.target.value)}
                  disabled={validating}
                  className="flex-1"
                />
                <Button type="submit" variant="outline" disabled={validating}>
                  {validating ? 'Validando…' : isInvalid && showConnectionDetails ? 'Reconectar' : 'Conectar'}
                </Button>
              </div>
              <FieldError id={`${inputId}-error`} message={error} />
            </form>
          )}
        </div>
      </div>
    </Card>
  );
}
